/* eslint-disable @next/next/no-img-element */
'use client';
import { useState } from 'react';
import Link from 'next/link';
import { BookOpenText, Trash } from 'lucide-react';
import { secondsToMinutesString } from '@/utils/vod';
import { useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';
import { TwoKOActivityFace } from './2xko/TwoKOActivityFace';
import { LeagueActivityFace } from './league/LeagueActivityFace';
import { Dota2ActivityFace } from './dota2/Dota2ActivityFace';
import { GameLogoIcon } from './logos/GameLogoIcon';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  AlertDialog,
  AlertDialogDescription,
  AlertDialogTitle,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogCancel,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogTrigger,
  AlertDialogOverlay,
  AlertDialogPortal,
} from './ui/alert-dialog';
import { VodPreview } from './web/VodPreview';
import { GradeStars } from './GradeStars';
import { CS2ActivityFace } from './cs2/CS2ActivityFace';
import { GGSTActivityFace } from './ggst/GGSTActivityFace';
import { parseTwoXKOMetablob } from '../../convex/metablobs/2xko/utils';
import { parseDota2Metablob } from '../../convex/metablobs/dota2/utils';
import { parseCS2Metablob } from '../../convex/metablobs/cs2/utils';

export type GetActivityItem = (typeof api.activities.get._returnType)['page'][0];

const GameSpecificFaces = ({
  activity,
  size = 96,
  variant = 'normal',
}: {
  activity: GetActivityItem;
  size?: number;
  variant?: 'normal' | 'slim';
}) => {
  switch (activity.gameId) {
    case '2xko':
      return <TwoKOActivityFace metablob={parseTwoXKOMetablob(activity.metablob)} size={size} />;
    case 'league-of-legends':
      return <LeagueActivityFace activity={activity} size={size} variant={variant} />;
    case 'dota2':
      return <Dota2ActivityFace metablob={parseDota2Metablob(activity.metablob)} size={size} variant={variant} />;
    case 'cs2':
      return <CS2ActivityFace metablob={parseCS2Metablob(activity.metablob)} size={size} variant={variant} />;
    case 'ggst':
      return <GGSTActivityFace activity={activity} size={size} variant={variant} />;
    default:
      return null;
  }
};

export const ActivityItem = ({
  activity,
  context = 'desktop',
}: {
  activity: GetActivityItem;
  context?: 'web' | 'desktop';
}) => {
  const deleteActivity = useMutation(api.activities.deleteActivity);
  const [deleteVideoFile, setDeleteVideoFile] = useState(true);
  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await deleteActivity({ id: activity._id as Id<'activities'> });
      if (context === 'desktop' && deleteVideoFile && activity.localVodRef) {
        window.native.vods?.deleteVOD(activity.localVodRef);
        setDeleteVideoFile(false);
      }
    } catch (error) {
      console.error('Failed to delete activity:', error);
      alert('Failed to delete activity');
    }
  };

  const niceActivityDuration = secondsToMinutesString((activity.durationMilliseconds || 0) / 1000);
  const size = 96;

  return (
    <div
      style={{
        height: `${size}px`,
        minHeight: `${size}px`,
      }}
      className="hover:bg-accent/50 transition-colors group overflow-hidden flex flex-row justify-between"
    >
      <Link href={`/${context}/activity/${activity._id}`} className="flex-1">
        <div className="flex flex-row gap-4 items-center pr-2 flex-1">
          <GameLogoIcon gameId={activity.gameId} size={size} />
          <div className="flex-1 flex-row flex gap-2 items-center">
            {context === 'web' && activity.localVodRef && (
              <VodPreview activityId={activity._id} height={size} width={1.8 * size} />
            )}
            {context !== 'web' && (
              <img
                src={`vod://vods/${btoa(activity.localVodRef?.replace('.mp4', '.jpg') || '')}`}
                alt="thumbnail"
                width={1.8 * size}
                height={size}
                style={{
                  width: `${1.8 * size}px`,
                  height: `${size}px`,
                }}
                className="flex-shrink-0 object-cover"
              />
            )}
            <GameSpecificFaces activity={activity} size={size} variant="normal" />
            <div className="flex flex-col gap-2">
              <div className="flex flex-row justify-between items-center">
                <div className="text-xl font-bold">{activity.title}</div>
                <GradeStars activity={activity} size="md" />
              </div>
              <div className="flex flex-row gap-2">
                {activity.description && <div className="text-muted-foreground">{activity.description}</div>}
                <div>
                  {activity.journals.slice(0, 2).map((journal) => (
                    <div key={journal._id}>{journal.text}</div>
                  ))}
                </div>
                {activity.journals.length > 2 && (
                  <div className="flex flex-row gap-1 items-center text-muted-foreground">
                    +{activity.journals.length - 2} <BookOpenText />
                  </div>
                )}
              </div>
              {activity.journals.length === 0 && <BookOpenText className="text-muted-foreground/25" />}
            </div>
          </div>
          <div className="w-[200px] text-muted-foreground">
            <div className="text-sm">{new Date(activity.created).toLocaleString()}</div>
            <div className="text-sm">{niceActivityDuration}</div>
          </div>
        </div>
      </Link>
      <div className="flex flex-row gap-4 items-center pr-2">
        <AlertDialog>
          <AlertDialogTrigger>
            <Trash />
          </AlertDialogTrigger>
          <AlertDialogPortal>
            <AlertDialogOverlay className="bg-black/80" />
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Activity: Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  <p className="mb-6">
                    Are you sure you want to delete this activity? If it was uploaded, the cloud video will also be
                    deleted.
                    <br />
                    This will also delete journal entries and comments associated with this activity.
                  </p>
                  {context === 'desktop' && (
                    <Label className="flex items-center gap-2 mt-5">
                      <Checkbox
                        checked={deleteVideoFile}
                        onCheckedChange={(checked) => setDeleteVideoFile(checked === true)}
                      />
                      Delete file: {activity.localVodRef}
                    </Label>
                  )}
                  {context === 'web' && <p>File will not be deleted from disk.</p>}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <Badge variant="destructive">This action cannot be undone</Badge>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogPortal>
        </AlertDialog>
      </div>
    </div>
  );
};
